import React from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Tooltip from "@mui/material/Tooltip";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ConnectWithoutContactIcon from "@mui/icons-material/ConnectWithoutContact";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewHeadlineIcon from "@mui/icons-material/ViewHeadline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import NotesIcon from "@mui/icons-material/Notes";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import LinkIcon from "@mui/icons-material/Link";
import LoadSpinner from "../../../utilities/LoadSpinner";
import SettingToggleCard from "./SettingToggleCard";
import DirectAssignabilityToggle, {
  setDirectAssignability,
} from "./DirectAssignabilityToggle";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import AiAssetGeneratorModal from "./AiAssetGeneratorModal";
import RoleplayPlayerModal from "./RoleplayPlayerModal";
import ContentSourcePicker, { formatSourceUrl } from "./ContentSourcePicker";
import { useReferenceSources } from "./useReferenceSources";
import { uploadLearningFile } from "./swipeDeckHelpers";
import { ROLEPLAY_TEMPLATES } from "./roleplayTemplates";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getRightworkAPI, postRightworkAPI } from "../requests/utilities/requests";
import {
  API_post_learning_roleplay,
  API_get_learning_roleplay_detail,
  API_delete_learning_roleplay,
  API_post_learning_roleplay_preview_knowledge,
  API_generate_roleplay_cover_with_ai,
} from "../../../constants";

const CRITERIA_MIN = 3;
const CRITERIA_MAX = 5;

// Mirror the backend's knowledge-source caps (learning_roleplay.py
// _KNOWLEDGE_MAX_*) so over-selection warns here instead of being trimmed
// silently on save.
const KNOWLEDGE_MAX_FILES = 25;
const KNOWLEDGE_MAX_SECTIONS = 10;
const KNOWLEDGE_MAX_MODULES = 5;
const KNOWLEDGE_PASTED_MAX_CHARS = 10000;

const style = {
  position: "absolute",
  inset: 0,
  margin: "auto",
  width: "94%",
  height: "92%",
  maxWidth: "1100px",
  maxHeight: "92vh",
  bgcolor: "background.paper",
  borderRadius: "12px",
  boxShadow: 24,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  border: "none",
};

// Shared TextField skins — mirrors QuizEditorModal's convention (white bg,
// 8px radius, violet focus ring).
const singleLineInputSx = {
  backgroundColor: "white",
  borderRadius: "8px",
  height: "42px",
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6d28d9" },
};
const multilineInputSx = {
  backgroundColor: "white",
  borderRadius: "8px",
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6d28d9" },
};

const labelClass = "block text-sm font-medium text-slate-800 mb-1.5";
const helpClass = "text-xs text-slate-500 mb-1.5";

// Realtime voices the backend allowlists (learning_roleplay.py
// ROLEPLAY_VOICES), labeled so an author can match the character's gender
// without listening first. The AI generator picks one to fit its persona;
// this select lets the author override or set one on hand-written scenarios.
const VOICE_DEFAULT = "cedar";
const VOICE_OPTIONS = [
  { value: "cedar", label: "Cedar · male" },
  { value: "echo", label: "Echo · male, steady" },
  { value: "ash", label: "Ash · male, warm" },
  { value: "verse", label: "Verse · male, expressive" },
  { value: "ballad", label: "Ballad · male, calm" },
  { value: "marin", label: "Marin · female" },
  { value: "coral", label: "Coral · female, upbeat" },
  { value: "sage", label: "Sage · female, calm" },
  { value: "shimmer", label: "Shimmer · female, bright" },
  { value: "alloy", label: "Alloy · neutral" },
];
const VOICE_VALUES = new Set(VOICE_OPTIONS.map((o) => o.value));

// Backend mints the durable id on save when missing; the "new_" prefix marks
// an unsaved row (its id is sent as undefined so the backend mints one) while
// still giving React a stable key.
const makeCriterion = () => ({
  id: `new_${Math.random().toString(36).slice(2, 10)}`,
  label: "",
  description: "",
});

function RoleplayEditorModal(props) {
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [persona, setPersona] = React.useState("");
  const [situation, setSituation] = React.useState("");
  const [behavioralDirectives, setBehavioralDirectives] = React.useState("");
  const [openingLine, setOpeningLine] = React.useState("");
  const [criteria, setCriteria] = React.useState([]);
  const [maxTurns, setMaxTurns] = React.useState(8);
  const [voice, setVoice] = React.useState(VOICE_DEFAULT);
  const [saveToProfile, setSaveToProfile] = React.useState(false);
  const [directlyAssignable, setDirectlyAssignable] = React.useState(false);
  // Loaded value, to detect whether the assignability setting changed on Save.
  const originalAssignableRef = React.useRef(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [sectionUsageCount, setSectionUsageCount] = React.useState(null);
  // Set when editing an existing roleplay whose detail failed to load. Blocks
  // the editor body + Save so a failed load can't overwrite the scenario.
  const [loadError, setLoadError] = React.useState(false);
  const [existingUuid, setExistingUuid] = React.useState(null);
  const [aiGenOpen, setAiGenOpen] = React.useState(false);
  const [previewOpen, setPreviewOpen] = React.useState(false);
  // Knowledge grounding sources: persisted on the scenario, resolved into a
  // fact digest at attempt start. Files ride the shared reference-sources
  // hook; section/module/paste selections are local (mirrors
  // AiAssetGeneratorModal, plus seed-from-saved which no other consumer
  // needs).
  const refs = useReferenceSources(props.homeCompany, props.open);
  const [knowledgeSections, setKnowledgeSections] = React.useState([]);
  const [knowledgeModules, setKnowledgeModules] = React.useState([]);
  const [knowledgePastedText, setKnowledgePastedText] = React.useState("");
  const [knowledgePickerOpen, setKnowledgePickerOpen] = React.useState(false);
  // Server-resolved names for saved sources (fallback chip labels + the
  // "source no longer available" state).
  const [knowledgeDisplay, setKnowledgeDisplay] = React.useState(null);
  // Digest fetched once when Preview opens with sources selected; passed to
  // the stateless preview endpoints via previewScenario.
  const [previewKnowledgeContext, setPreviewKnowledgeContext] = React.useState("");
  const [isPreviewLoading, setIsPreviewLoading] = React.useState(false);
  // Cover image: bound to the scenario on save via cover_file_uuid. AI-generated
  // (from persona+situation) or uploaded, mirroring the swipe-deck cover.
  const [coverFileUuid, setCoverFileUuid] = React.useState(null);
  const [coverUrl, setCoverUrl] = React.useState(null);
  const [isGeneratingCover, setIsGeneratingCover] = React.useState(false);
  const [isUploadingCover, setIsUploadingCover] = React.useState(false);
  const coverFileInputRef = React.useRef(null);
  // Bumped every time the modal opens. A cover generate/upload captures the
  // current token and, on completion, no-ops if the modal was reopened (for a
  // different scenario) meanwhile — otherwise the reused modal instance would
  // let a stale job write its cover into the new form and save it onto the
  // wrong roleplay. Mirrors useReferenceSources' seqRef guard.
  const coverJobTokenRef = React.useRef(0);

  // Seed the content fields from an AI draft or a template. Touches only the
  // scenario content — never save_to_profile / assignability / the uuid, so
  // regenerating over an existing scenario keeps its settings and identity.
  const seedFromDraft = (draft) => {
    setName(draft.name || "");
    setDescription(draft.description || "");
    setPersona(draft.persona || "");
    setSituation(draft.situation || "");
    setBehavioralDirectives(draft.behavioral_directives || "");
    setOpeningLine(draft.opening_line || "");
    setCriteria(
      (draft.criteria || []).map((c) => ({
        // AI drafts carry server-minted ids (kept); template rows have none,
        // so mint local new_ keys and let the backend assign durable ids.
        id: c.id || `new_${Math.random().toString(36).slice(2, 10)}`,
        label: c.label || "",
        description: c.description || "",
      }))
    );
    setMaxTurns(draft.max_turns || 8);
    if (VOICE_VALUES.has(draft.voice)) setVoice(draft.voice);
  };

  // The template strip only shows on a pristine create form — once anything
  // meaningful is typed (or an AI draft landed), it gets out of the way.
  const isPristine =
    !persona.trim() && !situation.trim() && !openingLine.trim() && !name.trim();

  const applySeed = (r) => {
    setName(r.name || "");
    setDescription(r.description || "");
    setPersona(r.persona || "");
    setSituation(r.situation || "");
    setBehavioralDirectives(r.behavioral_directives || "");
    setOpeningLine(r.opening_line || "");
    setCriteria(
      (r.criteria || []).map((c) => ({
        id: c.id,
        label: c.label || "",
        description: c.description || "",
      }))
    );
    setMaxTurns(r.max_turns || 8);
    setVoice(VOICE_VALUES.has(r.voice) ? r.voice : VOICE_DEFAULT);
    setSaveToProfile(!!r.save_to_profile);
    setDirectlyAssignable(!!r.is_directly_assignable);
    originalAssignableRef.current = !!r.is_directly_assignable;
    const ks = r.knowledge_sources || {};
    setKnowledgeSections(ks.section_uuids || []);
    setKnowledgeModules(ks.module_uuids || []);
    setKnowledgePastedText(ks.pasted_text || "");
    refs.seedFiles(ks.file_uuids || []);
    setKnowledgeDisplay(r.knowledge_sources_display || null);
    setCoverFileUuid(r.cover?.uuid || null);
    setCoverUrl(r.cover?.url || null);
  };

  // Seed state when the modal opens. When editing from the library we get just
  // a { uuid }, so load the full detail from the backend.
  React.useEffect(() => {
    if (!props.open) return;
    const s = props.modalDataRoleplay || {};
    // Invalidate any cover job still in flight from a previous open so its late
    // completion can't write into this (reused) modal instance.
    coverJobTokenRef.current += 1;
    setConfirmDelete(false);
    setSectionUsageCount(null);
    setLoadError(false);
    setExistingUuid(s.uuid || null);
    setKnowledgePickerOpen(false);
    setPreviewKnowledgeContext("");
    setIsGeneratingCover(false);
    setIsUploadingCover(false);
    // Clear any prior session's file/folder selection before seeding — the
    // hook instance survives close/reopen, and seedFiles alone wouldn't
    // clear a leftover folder selection.
    refs.reset();
    if (s.uuid) {
      setIsLoading(true);
      (async () => {
        try {
          const res = await getRightworkAPI(API_get_learning_roleplay_detail, {
            company_id: props.homeCompany,
            learning_roleplay_uuid: s.uuid,
            include_section_usage: true,
          });
          const payload =
            res?.status === 200
              ? res.data?.redux?.payload?.learning_roleplay
              : null;
          if (!payload) throw new Error("Practice scenario detail not returned");
          applySeed(payload);
          if (payload.section_usage_count !== undefined) {
            setSectionUsageCount(payload.section_usage_count);
          }
        } catch (error) {
          setLoadError(true);
          toast.error("Couldn't load this practice scenario. Please try again.");
        } finally {
          setIsLoading(false);
        }
      })();
    } else {
      // Create mode: blank scenario with the minimum criteria rows ready.
      applySeed({});
      setCriteria([makeCriterion(), makeCriterion(), makeCriterion()]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  const setCriterion = (idx, key, value) =>
    setCriteria((prev) =>
      prev.map((c, i) => (i === idx ? { ...c, [key]: value } : c))
    );

  const handleKnowledgeSectionToggle = (sectionUuid) =>
    setKnowledgeSections((prev) =>
      prev.includes(sectionUuid)
        ? prev.filter((uuid) => uuid !== sectionUuid)
        : [...prev, sectionUuid]
    );
  const handleKnowledgeModuleToggle = (moduleUuid) =>
    setKnowledgeModules((prev) =>
      prev.includes(moduleUuid)
        ? prev.filter((uuid) => uuid !== moduleUuid)
        : [...prev, moduleUuid]
    );

  const hasKnowledgeSources =
    refs.expandedFileUuids.length > 0 ||
    knowledgeSections.length > 0 ||
    knowledgeModules.length > 0 ||
    knowledgePastedText.trim().length > 0;

  const coverBusy = isGeneratingCover || isUploadingCover;
  // Generate a cover from the unsaved persona/situation. Needs both filled so
  // the image depicts the right person in the right scene.
  const canGenerateCover = persona.trim() && situation.trim() && !coverBusy;

  const handleGenerateCover = async () => {
    if (!canGenerateCover) return;
    const token = coverJobTokenRef.current;
    setIsGeneratingCover(true);
    try {
      const res = await postRightworkAPI(API_generate_roleplay_cover_with_ai, {
        company_id: props.homeCompany,
        persona: persona.trim(),
        situation: situation.trim(),
        name: name.trim(),
      });
      const file = res.data?.redux?.payload?.learning_file;
      if (!file?.uuid) throw new Error("No cover image returned");
      if (token !== coverJobTokenRef.current) return; // modal reopened — stale
      setCoverFileUuid(file.uuid);
      setCoverUrl(file.url || null);
    } catch (error) {
      if (token !== coverJobTokenRef.current) return;
      toast.error(
        error?.response?.data?.system?.message ||
          "Couldn't generate a cover image. Please try again."
      );
    } finally {
      // Only touch the busy flag if this job still owns the form; otherwise a
      // stale job would clear the new form's spinner.
      if (token === coverJobTokenRef.current) setIsGeneratingCover(false);
    }
  };

  const handleCoverUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    const token = coverJobTokenRef.current;
    setIsUploadingCover(true);
    try {
      const { uuid, url } = await uploadLearningFile(props.homeCompany, file);
      if (token !== coverJobTokenRef.current) return; // modal reopened — stale
      setCoverFileUuid(uuid);
      setCoverUrl(url || null);
    } catch (error) {
      if (token !== coverJobTokenRef.current) return;
      toast.error("Couldn't upload that image. Please try again.");
    } finally {
      if (token === coverJobTokenRef.current) setIsUploadingCover(false);
    }
  };

  const handleClearCover = () => {
    setCoverFileUuid(null);
    setCoverUrl(null);
  };

  const filledCriteria = criteria.filter((c) => c.label.trim());
  // Preview needs a playable character + something to grade against. Built
  // from CURRENT editor state, so unsaved changes are exactly what you test.
  const canPreview =
    !isLoading &&
    !loadError &&
    persona.trim() &&
    situation.trim() &&
    openingLine.trim() &&
    filledCriteria.length >= 1;
  const previewScenario = {
    name: name.trim() || "Practice Scenario",
    persona: persona.trim(),
    situation: situation.trim(),
    behavioral_directives: behavioralDirectives.trim(),
    opening_line: openingLine.trim(),
    criteria: filledCriteria.map((c) => ({
      label: c.label.trim(),
      description: (c.description || "").trim(),
    })),
    max_turns: maxTurns,
    voice,
    knowledge_context: previewKnowledgeContext,
    cover_image: coverUrl,
  };

  // Preview against unsaved knowledge sources: resolve them into the digest
  // once per preview open (not per turn), then hand the string to the
  // stateless preview endpoints. Resolution failure degrades to an
  // ungrounded preview — same posture as a real attempt start.
  const handleOpenPreview = async () => {
    if (!canPreview || isPreviewLoading) return;
    let knowledgeContext = "";
    if (hasKnowledgeSources) {
      setIsPreviewLoading(true);
      try {
        const res = await postRightworkAPI(
          API_post_learning_roleplay_preview_knowledge,
          {
            company_id: props.homeCompany,
            learning_section_uuids: JSON.stringify(knowledgeSections),
            learning_module_uuids: JSON.stringify(knowledgeModules),
            file_upload_uuids: JSON.stringify(refs.expandedFileUuids),
            pasted_text: knowledgePastedText.trim(),
            situation: situation.trim(),
          }
        );
        const payload = res.data?.redux?.payload || {};
        (payload.warnings || []).forEach((w) =>
          toast.warn(w, { autoClose: 6000 })
        );
        knowledgeContext = payload.knowledge_context || "";
      } catch (error) {
        toast.warn(
          "Couldn't load the knowledge sources — previewing without them.",
          { autoClose: 4000 }
        );
      } finally {
        setIsPreviewLoading(false);
      }
    }
    setPreviewKnowledgeContext(knowledgeContext);
    setPreviewOpen(true);
  };
  const canSave =
    !isLoading &&
    !loadError &&
    // Block saving while a cover upload/generation is in flight: handleSave
    // would post the stale coverFileUuid and close the modal, dropping the
    // intended cover and orphaning the file the async op is about to return.
    !coverBusy &&
    persona.trim() &&
    situation.trim() &&
    openingLine.trim() &&
    filledCriteria.length >= CRITERIA_MIN &&
    filledCriteria.length <= CRITERIA_MAX;

  const handleSave = async () => {
    if (!canSave || isSaving) return;
    setIsSaving(true);
    try {
      const res = await postRightworkAPI(API_post_learning_roleplay, {
        company_id: props.homeCompany,
        learning_roleplay_uuid: existingUuid || undefined,
        name: name.trim() || "Practice Scenario",
        description: description.trim(),
        persona: persona.trim(),
        situation: situation.trim(),
        behavioral_directives: behavioralDirectives.trim(),
        opening_line: openingLine.trim(),
        criteria: JSON.stringify(
          filledCriteria.map((c) => ({
            // New rows let the backend mint a durable id; existing rows keep
            // theirs so historical scorecards stay keyed correctly.
            id: String(c.id || "").startsWith("new_") ? undefined : c.id,
            label: c.label.trim(),
            description: (c.description || "").trim(),
          }))
        ),
        max_turns: maxTurns,
        voice,
        save_to_profile: saveToProfile,
        // Folder selections collapse to their file uuids at save (the stored
        // shape has no folder_uuids), so a reload shows individual file chips.
        knowledge_sources: JSON.stringify({
          file_uuids: refs.expandedFileUuids,
          section_uuids: knowledgeSections,
          module_uuids: knowledgeModules,
          pasted_text: knowledgePastedText.trim(),
        }),
        // Presence-gated on the backend; "" clears the cover.
        cover_file_uuid: coverFileUuid || "",
      });
      const saved = res.data?.redux?.payload?.learning_roleplay;
      toast.success("Practice scenario saved");
      // Persist the "make directly assignable" setting on Save (only when it
      // changed) — like every other setting, mirroring FormEditorModal.
      if (
        saved?.uuid &&
        !!directlyAssignable !== originalAssignableRef.current
      ) {
        await setDirectAssignability({
          companyId: props.homeCompany,
          assetType: "roleplay",
          assetUuid: saved.uuid,
          isAssignable: !!directlyAssignable,
        });
        originalAssignableRef.current = !!directlyAssignable;
      }
      if (props.onSaveSuccess) props.onSaveSuccess(saved);
      props.handleClose();
    } catch (error) {
      toast.error(
        error?.response?.data?.system?.message ||
          "Couldn't save the practice scenario. Please try again."
      );
    } finally {
      setIsSaving(false);
    }
  };

  const sectionsData = props.sectionsData || [];
  const modulesData = props.modulesData || [];
  const sectionByUuid = React.useMemo(() => {
    const m = new Map();
    sectionsData.forEach((s) => m.set(s.uuid, s));
    return m;
  }, [sectionsData]);
  const moduleByUuid = React.useMemo(() => {
    const m = new Map();
    modulesData.forEach((mm) => m.set(mm.uuid, mm));
    return m;
  }, [modulesData]);
  // Server-resolved fallback labels for saved sources the live lists can't
  // name (mid-refresh, or deleted/moved out of an assistant-searchable
  // folder — the latter render as "no longer available").
  const knowledgeDisplayFiles = React.useMemo(
    () => new Map((knowledgeDisplay?.files || []).map((f) => [f.uuid, f])),
    [knowledgeDisplay]
  );
  const knowledgeDisplaySections = React.useMemo(
    () => new Map((knowledgeDisplay?.sections || []).map((s) => [s.uuid, s])),
    [knowledgeDisplay]
  );
  const knowledgeDisplayModules = React.useMemo(
    () => new Map((knowledgeDisplay?.modules || []).map((m) => [m.uuid, m])),
    [knowledgeDisplay]
  );
  const resolvedFileUuids = new Set(refs.selectedFileObjects.map((f) => f.uuid));
  const unresolvedKnowledgeFiles = refs.selectedFiles.filter(
    (u) => !resolvedFileUuids.has(u)
  );
  const knowledgeSelectedCount =
    refs.selectedFolderObjects.length +
    refs.selectedFiles.length +
    knowledgeSections.length +
    knowledgeModules.length +
    (knowledgePastedText.trim() ? 1 : 0);
  const knowledgeCapWarnings = [];
  if (refs.expandedFileUuids.length > KNOWLEDGE_MAX_FILES) {
    knowledgeCapWarnings.push(
      `only the first ${KNOWLEDGE_MAX_FILES} documents (folders count their files) will be used`
    );
  }
  if (knowledgeSections.length > KNOWLEDGE_MAX_SECTIONS) {
    knowledgeCapWarnings.push(
      `only the first ${KNOWLEDGE_MAX_SECTIONS} sections will be used`
    );
  }
  if (knowledgeModules.length > KNOWLEDGE_MAX_MODULES) {
    knowledgeCapWarnings.push(
      `only the first ${KNOWLEDGE_MAX_MODULES} modules will be used`
    );
  }
  if (knowledgePastedText.length > KNOWLEDGE_PASTED_MAX_CHARS) {
    knowledgeCapWarnings.push(
      `pasted text will be trimmed to ${KNOWLEDGE_PASTED_MAX_CHARS.toLocaleString()} characters`
    );
  }

  const handleDelete = async () => {
    if (!existingUuid || isDeleting) return;
    setIsDeleting(true);
    try {
      await postRightworkAPI(API_delete_learning_roleplay, {
        company_id: props.homeCompany,
        learning_roleplay_uuid: existingUuid,
      });
      toast.success("Practice scenario deleted");
      if (props.onSaveSuccess) props.onSaveSuccess(null);
      props.handleClose();
    } catch (error) {
      toast.error(
        error?.response?.data?.system?.message ||
          "Couldn't delete the practice scenario. Please try again."
      );
    } finally {
      setIsDeleting(false);
      setConfirmDelete(false);
    }
  };

  return (
    <>
    <Modal open={props.open} onClose={props.handleClose}>
      <Box sx={style}>
        {/* Header */}
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-violet-100">
              <ConnectWithoutContactIcon className="text-violet-600" style={{ fontSize: 20 }} />
            </div>
            <h3 className="text-lg font-bold text-slate-900">
              {existingUuid ? "Edit Practice Scenario" : "Create Practice Scenario"}
            </h3>
            {sectionUsageCount !== null && sectionUsageCount > 0 && (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                Used in {sectionUsageCount} section
                {sectionUsageCount === 1 ? "" : "s"}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Tooltip
              title={
                canPreview
                  ? "Test-drive this scenario (nothing is recorded)"
                  : "Fill in the character, situation, opening line, and at least one criterion to preview"
              }
            >
              <span>
                <button
                  type="button"
                  onClick={handleOpenPreview}
                  disabled={!canPreview || isPreviewLoading}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-slate-700 border border-slate-200 hover:bg-slate-50 disabled:opacity-40 transition-colors"
                >
                  <VisibilityIcon style={{ fontSize: 16 }} />
                  {isPreviewLoading ? "Preparing…" : "Preview"}
                </button>
              </span>
            </Tooltip>
            <button
              type="button"
              onClick={() => setAiGenOpen(true)}
              disabled={isLoading || loadError}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-violet-700 border border-violet-200 hover:bg-violet-50 disabled:opacity-40 transition-colors"
            >
              <AutoAwesomeIcon style={{ fontSize: 16 }} />
              {persona.trim() && situation.trim()
                ? "Edit with AI"
                : "Generate with AI"}
            </button>
            <button
              type="button"
              onClick={props.handleClose}
              title="Close"
              className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-slate-700 transition-colors shrink-0"
            >
              <CloseIcon />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 bg-white">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <LoadSpinner />
            </div>
          ) : loadError ? (
            <p className="text-sm text-slate-500 text-center py-10">
              This practice scenario couldn't be loaded. Close and try again.
            </p>
          ) : (
            <div className="max-w-3xl mx-auto space-y-6">
              {/* Starter templates — pristine create form only */}
              {!existingUuid && isPristine && (
                <div>
                  <label className={labelClass}>Start from a template</label>
                  <p className={helpClass}>
                    Pick a common scenario and localize it, describe your own
                    below, or use Generate with AI.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {ROLEPLAY_TEMPLATES.map((t) => (
                      <button
                        key={t.key}
                        type="button"
                        onClick={() => seedFromDraft(t)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 bg-slate-50 hover:border-violet-300 hover:bg-violet-50 text-sm text-slate-700 transition-colors"
                      >
                        <span>{t.emoji}</span>
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className={labelClass}>Name</label>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder='e.g. "Cold entrée complaint"'
                  InputProps={{ sx: singleLineInputSx }}
                />
              </div>

              <div>
                <label className={labelClass}>Description</label>
                <p className={helpClass}>
                  Shown to the employee on the practice scenario card, alongside what
                  they'll be coached on.
                </p>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  variant="outlined"
                  size="small"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Practice handling a frustrated guest whose food came out late."
                  InputProps={{ sx: multilineInputSx }}
                />
              </div>

              <div>
                <label className={labelClass}>Cover image</label>
                <p className={helpClass}>
                  A photo of the character in the scene, shown on the scenario
                  card and in the conversation. Generate one from the character
                  and situation below, or upload your own.
                </p>
                <input
                  ref={coverFileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleCoverUpload}
                />
                {coverUrl ? (
                  <div className="relative w-full max-w-md overflow-hidden rounded-xl border border-slate-200">
                    <img
                      src={coverUrl}
                      alt="Scenario cover"
                      className="w-full aspect-[3/2] object-cover"
                    />
                    {coverBusy && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/70">
                        <LoadSpinner />
                      </div>
                    )}
                    <div className="absolute right-2 top-2 flex gap-1.5">
                      <button
                        type="button"
                        onClick={handleGenerateCover}
                        disabled={!canGenerateCover}
                        title="Regenerate with AI"
                        className="inline-flex items-center gap-1 rounded-lg bg-white/90 px-2 py-1 text-xs font-medium text-violet-700 shadow-sm hover:bg-white disabled:opacity-40"
                      >
                        <AutoAwesomeIcon style={{ fontSize: 14 }} />
                        Regenerate
                      </button>
                      <button
                        type="button"
                        onClick={handleClearCover}
                        disabled={coverBusy}
                        title="Remove cover"
                        className="inline-flex items-center justify-center rounded-lg bg-white/90 p-1 text-slate-600 shadow-sm hover:bg-white hover:text-red-600 disabled:opacity-40"
                      >
                        <CloseIcon style={{ fontSize: 16 }} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center gap-2">
                    <Tooltip
                      title={
                        canGenerateCover
                          ? "Generate a cover from the character + situation"
                          : "Fill in the character and situation first"
                      }
                    >
                      <span>
                        <button
                          type="button"
                          onClick={handleGenerateCover}
                          disabled={!canGenerateCover}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-sm font-medium text-violet-700 hover:bg-violet-100 disabled:opacity-40"
                        >
                          {isGeneratingCover ? (
                            <LoadSpinner />
                          ) : (
                            <AutoAwesomeIcon style={{ fontSize: 16 }} />
                          )}
                          {isGeneratingCover ? "Generating…" : "Generate with AI"}
                        </button>
                      </span>
                    </Tooltip>
                    <button
                      type="button"
                      onClick={() => coverFileInputRef.current?.click()}
                      disabled={coverBusy}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                    >
                      {isUploadingCover ? (
                        <LoadSpinner />
                      ) : (
                        <AddPhotoAlternateOutlinedIcon style={{ fontSize: 16 }} />
                      )}
                      {isUploadingCover ? "Uploading…" : "Upload"}
                    </button>
                  </div>
                )}
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-4">
                <div className="text-sm font-semibold text-slate-900">
                  The character{" "}
                  <span className="font-normal text-slate-500">
                    (never shown to the employee)
                  </span>
                </div>
                <div>
                  <label className={labelClass}>
                    Who the AI plays <span className="text-red-500">*</span>
                  </label>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    variant="outlined"
                    size="small"
                    value={persona}
                    onChange={(e) => setPersona(e.target.value)}
                    placeholder="Dana, a regular guest whose entrée came out 30 minutes late and is now cold. Frustrated but reasonable."
                    InputProps={{ sx: multilineInputSx }}
                  />
                </div>
                <div>
                  <label className={labelClass}>Voice</label>
                  <p className={helpClass}>
                    How the character sounds in spoken practice. AI-generated
                    scenarios pick one to match automatically.
                  </p>
                  <TextField
                    select
                    variant="outlined"
                    size="small"
                    value={voice}
                    onChange={(e) => setVoice(e.target.value)}
                    InputProps={{ sx: { ...singleLineInputSx, width: "220px" } }}
                  >
                    {VOICE_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </div>
                <div>
                  <label className={labelClass}>
                    The situation <span className="text-red-500">*</span>
                  </label>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    variant="outlined"
                    size="small"
                    value={situation}
                    onChange={(e) => setSituation(e.target.value)}
                    placeholder="A busy Friday dinner service. The kitchen fell behind and Dana's table was forgotten. The employee is the server responsible for the table."
                    InputProps={{ sx: multilineInputSx }}
                  />
                </div>
                <div>
                  <label className={labelClass}>Behavior notes</label>
                  <p className={helpClass}>
                    Optional directives for how the character reacts, e.g.
                    "Escalates if not acknowledged in the first reply; softens
                    if offered a manager."
                  </p>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    variant="outlined"
                    size="small"
                    value={behavioralDirectives}
                    onChange={(e) => setBehavioralDirectives(e.target.value)}
                    InputProps={{ sx: multilineInputSx }}
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    Opening line <span className="text-red-500">*</span>
                  </label>
                  <p className={helpClass}>
                    The character's first message. Every attempt starts the same
                    way.
                  </p>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    variant="outlined"
                    size="small"
                    value={openingLine}
                    onChange={(e) => setOpeningLine(e.target.value)}
                    placeholder="Excuse me, we ordered over half an hour ago, and this just got here cold. This is really disappointing."
                    InputProps={{ sx: multilineInputSx }}
                  />
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
                <div>
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold text-slate-900">
                      Knowledge sources{" "}
                      <span className="font-normal text-slate-500">(optional)</span>
                    </div>
                    <span className="text-xs text-slate-500">
                      {knowledgeSelectedCount === 0
                        ? "None selected"
                        : `${knowledgeSelectedCount} selected`}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Attach real menus, policies, or training content. The
                    character uses these facts in the conversation, and the AI
                    coach checks the employee's answers against them. Never
                    shown to the employee.
                  </p>
                </div>

                {knowledgeSelectedCount > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {refs.selectedFolderObjects.map((f) => (
                      <span
                        key={`kfld-${f.uuid}`}
                        className="inline-flex items-center gap-1.5 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded px-2 py-1"
                      >
                        <FolderOutlinedIcon style={{ fontSize: 13 }} className="text-amber-600" />
                        <span className="font-medium">{f.name}</span>
                        <span className="text-amber-700/70">
                          · {f.file_count} file{f.file_count === 1 ? "" : "s"}
                        </span>
                        <button
                          onClick={() => refs.toggleFolder(f.uuid)}
                          className="ml-0.5 text-amber-700 hover:text-amber-900"
                          aria-label={`Remove ${f.name}`}
                        >
                          <CloseIcon style={{ fontSize: 12 }} />
                        </button>
                      </span>
                    ))}
                    {knowledgeModules.map((u) => {
                      const m = moduleByUuid.get(u);
                      const fallback = knowledgeDisplayModules.get(u);
                      const unavailable = !m && !fallback;
                      const name =
                        m?.name || fallback?.name || "Removed module";
                      return (
                        <span
                          key={`km-${u}`}
                          className={`inline-flex items-center gap-1.5 text-xs rounded px-2 py-1 border ${
                            unavailable
                              ? "bg-red-50 border-red-200 text-red-800"
                              : "bg-violet-50 border-violet-200 text-violet-900"
                          }`}
                        >
                          <ViewModuleIcon
                            style={{ fontSize: 13 }}
                            className={unavailable ? "text-red-500" : "text-violet-600"}
                          />
                          <span className="font-medium">
                            {name}
                            {unavailable ? " (no longer available)" : ""}
                          </span>
                          <button
                            onClick={() => handleKnowledgeModuleToggle(u)}
                            className={`ml-0.5 ${
                              unavailable
                                ? "text-red-600 hover:text-red-800"
                                : "text-violet-700 hover:text-violet-900"
                            }`}
                            aria-label={`Remove ${name}`}
                          >
                            <CloseIcon style={{ fontSize: 12 }} />
                          </button>
                        </span>
                      );
                    })}
                    {knowledgeSections.map((u) => {
                      const s = sectionByUuid.get(u);
                      const fallback = knowledgeDisplaySections.get(u);
                      const unavailable = !s && !fallback;
                      const name =
                        s?.name || fallback?.name || "Removed section";
                      return (
                        <span
                          key={`ks-${u}`}
                          className={`inline-flex items-center gap-1.5 text-xs rounded px-2 py-1 border ${
                            unavailable
                              ? "bg-red-50 border-red-200 text-red-800"
                              : "bg-white border-slate-200 text-slate-700"
                          }`}
                        >
                          <ViewHeadlineIcon
                            style={{ fontSize: 13 }}
                            className={unavailable ? "text-red-500" : "text-slate-500"}
                          />
                          <span className="font-medium">
                            {name}
                            {unavailable ? " (no longer available)" : ""}
                          </span>
                          <button
                            onClick={() => handleKnowledgeSectionToggle(u)}
                            className={`ml-0.5 ${
                              unavailable
                                ? "text-red-600 hover:text-red-800"
                                : "text-slate-500 hover:text-slate-700"
                            }`}
                            aria-label={`Remove ${name}`}
                          >
                            <CloseIcon style={{ fontSize: 12 }} />
                          </button>
                        </span>
                      );
                    })}
                    {refs.selectedFileObjects.map((f) => {
                      const url = formatSourceUrl(f.source_url);
                      const label = url || f.name || "Document";
                      return (
                        <span
                          key={`kf-${f.uuid}`}
                          className="inline-flex items-center gap-1.5 text-xs bg-sky-50 border border-sky-200 text-sky-900 rounded px-2 py-1"
                        >
                          {url ? (
                            <LinkIcon style={{ fontSize: 13 }} className="text-sky-600" />
                          ) : (
                            <DescriptionOutlinedIcon style={{ fontSize: 13 }} className="text-sky-600" />
                          )}
                          <span className="font-medium">{label}</span>
                          <button
                            onClick={() => refs.toggleFile(f.uuid)}
                            className="ml-0.5 text-sky-700 hover:text-sky-900"
                            aria-label={`Remove ${label}`}
                          >
                            <CloseIcon style={{ fontSize: 12 }} />
                          </button>
                        </span>
                      );
                    })}
                    {unresolvedKnowledgeFiles.map((u) => {
                      // Saved file not in the live searchable list: name it from
                      // the server-resolved display payload (mid-refresh), or
                      // flag it — deleted/moved sources contribute nothing at
                      // attempt start.
                      const fallback = knowledgeDisplayFiles.get(u);
                      const unavailable = !fallback || fallback.available === false;
                      return (
                        <span
                          key={`kfm-${u}`}
                          className={`inline-flex items-center gap-1.5 text-xs rounded px-2 py-1 border ${
                            unavailable
                              ? "bg-red-50 border-red-200 text-red-800"
                              : "bg-sky-50 border-sky-200 text-sky-900"
                          }`}
                        >
                          <DescriptionOutlinedIcon
                            style={{ fontSize: 13 }}
                            className={unavailable ? "text-red-500" : "text-sky-600"}
                          />
                          <span className="font-medium">
                            {fallback?.name || "Removed document"}
                            {unavailable ? " (no longer available)" : ""}
                          </span>
                          <button
                            onClick={() => refs.toggleFile(u)}
                            className={`ml-0.5 ${
                              unavailable
                                ? "text-red-600 hover:text-red-800"
                                : "text-sky-700 hover:text-sky-900"
                            }`}
                            aria-label="Remove source"
                          >
                            <CloseIcon style={{ fontSize: 12 }} />
                          </button>
                        </span>
                      );
                    })}
                    {knowledgePastedText.trim().length > 0 && (
                      <span className="inline-flex items-center gap-1.5 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded px-2 py-1">
                        <NotesIcon style={{ fontSize: 13 }} className="text-amber-600" />
                        <span className="font-medium">Pasted text</span>
                        <button
                          onClick={() => setKnowledgePastedText("")}
                          className="ml-0.5 text-amber-700 hover:text-amber-900"
                          aria-label="Remove pasted text"
                        >
                          <CloseIcon style={{ fontSize: 12 }} />
                        </button>
                      </span>
                    )}
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => setKnowledgePickerOpen(true)}
                  className="w-full px-3 py-2 border border-dashed border-slate-300 hover:border-violet-400 hover:bg-violet-50 text-violet-700 rounded-lg font-medium text-sm flex items-center justify-center gap-2 transition-colors"
                >
                  <AddIcon style={{ fontSize: 16 }} />
                  Add sources
                </button>

                {knowledgeCapWarnings.length > 0 && (
                  <p className="text-xs text-amber-700">
                    Selection exceeds the limit: {knowledgeCapWarnings.join("; ")}.
                  </p>
                )}
              </div>

              <div>
                <label className={labelClass}>
                  Success criteria <span className="text-red-500">*</span>
                </label>
                <p className={helpClass}>
                  {CRITERIA_MIN}–{CRITERIA_MAX} things a strong response does.
                  The AI coaches the employee against each one after the
                  conversation.
                </p>
                <div className="space-y-3">
                  {criteria.map((c, idx) => (
                    <div
                      key={c.id}
                      className="rounded-lg border border-slate-200 bg-slate-50 p-3 flex items-start gap-3"
                    >
                      <div className="flex-1 space-y-2">
                        <TextField
                          fullWidth
                          variant="outlined"
                          size="small"
                          value={c.label}
                          onChange={(e) => setCriterion(idx, "label", e.target.value)}
                          placeholder={
                            idx === 0
                              ? "Apologize without making excuses"
                              : idx === 1
                              ? "Offer a concrete fix"
                              : "Escalate appropriately"
                          }
                          InputProps={{ sx: singleLineInputSx }}
                        />
                        <TextField
                          fullWidth
                          multiline
                          minRows={1}
                          variant="outlined"
                          size="small"
                          value={c.description}
                          onChange={(e) =>
                            setCriterion(idx, "description", e.target.value)
                          }
                          placeholder="Optional detail for the AI coach: what does meeting this look like?"
                          InputProps={{ sx: multilineInputSx }}
                        />
                      </div>
                      <Tooltip title="Remove criterion">
                        <span>
                          <IconButton
                            size="small"
                            disabled={criteria.length <= CRITERIA_MIN}
                            onClick={() =>
                              setCriteria((prev) => prev.filter((_, i) => i !== idx))
                            }
                            sx={{
                              mt: "4px",
                              color: "#94a3b8",
                              "&:hover": { color: "#ef4444", backgroundColor: "#fef2f2" },
                            }}
                          >
                            <DeleteOutlineIcon style={{ fontSize: 18 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </div>
                  ))}
                </div>
                {criteria.length < CRITERIA_MAX && (
                  <button
                    type="button"
                    className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-violet-700 hover:text-violet-800"
                    onClick={() => setCriteria((prev) => [...prev, makeCriterion()])}
                  >
                    <AddIcon style={{ fontSize: 18 }} />
                    Add criterion
                  </button>
                )}
              </div>

              <div>
                <label className={labelClass}>Conversation length</label>
                <TextField
                  variant="outlined"
                  size="small"
                  type="number"
                  value={maxTurns}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    setMaxTurns(Number.isNaN(v) ? 8 : Math.max(2, Math.min(v, 15)));
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">replies max</InputAdornment>
                    ),
                    sx: { ...singleLineInputSx, width: "180px" },
                  }}
                  inputProps={{ min: 2, max: 15 }}
                />
              </div>

              <SettingToggleCard
                checked={saveToProfile}
                onChange={(checked) => setSaveToProfile(checked)}
                title="Save to employee profile"
                description="Keep each employee's latest result on their profile's Records tab."
              />

              <DirectAssignabilityToggle
                value={directlyAssignable}
                onChange={setDirectlyAssignable}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-between shrink-0 bg-white">
          <div>
            {existingUuid &&
              (confirmDelete ? (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-600">Delete this practice scenario?</span>
                  <button
                    className="px-3 py-1.5 rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                    disabled={isDeleting}
                    onClick={handleDelete}
                  >
                    {isDeleting ? "Deleting…" : "Delete"}
                  </button>
                  <button
                    className="px-3 py-1.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-gray-100"
                    onClick={() => setConfirmDelete(false)}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  className="px-3 py-1.5 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50"
                  onClick={() => setConfirmDelete(true)}
                >
                  Delete
                </button>
              ))}
          </div>
          <div className="flex items-center gap-3">
            <button
              className="px-5 py-2 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg font-medium text-sm"
              onClick={props.handleClose}
            >
              Cancel
            </button>
            <button
              className={`px-5 py-2 rounded-lg font-medium text-sm inline-flex items-center gap-2 ${
                canSave && !isSaving
                  ? "bg-violet-600 hover:bg-violet-700 text-white"
                  : "bg-gray-200 text-gray-400 cursor-not-allowed"
              }`}
              disabled={!canSave || isSaving}
              onClick={handleSave}
            >
              {isSaving && <LoadSpinner />}
              Save Practice Scenario
            </button>
          </div>
        </div>
      </Box>
    </Modal>
    <AiAssetGeneratorModal
      variant="roleplay"
      open={aiGenOpen}
      handleClose={() => setAiGenOpen(false)}
      homeCompany={props.homeCompany}
      sectionsData={props.sectionsData || []}
      modulesData={props.modulesData || []}
      onGenerated={seedFromDraft}
      // Current editor state, so the modal can offer "edit with AI" (a
      // targeted revision) once there's a playable character to revise.
      // Criteria ids ride along ("" for unsaved rows, mirroring save) so
      // kept criteria come back with their durable ids intact.
      currentAsset={
        persona.trim() && situation.trim()
          ? {
              name: name.trim(),
              description: description.trim(),
              persona: persona.trim(),
              situation: situation.trim(),
              behavioral_directives: behavioralDirectives.trim(),
              opening_line: openingLine.trim(),
              criteria: filledCriteria.map((c) => ({
                id: String(c.id || "").startsWith("new_") ? "" : c.id,
                label: c.label.trim(),
                description: (c.description || "").trim(),
              })),
              max_turns: maxTurns,
              voice,
            }
          : null
      }
    />
    {/* Sibling, not child, of the outer Modal — nesting a second Modal inside
        MUI's Modal breaks its focus-trap utility. */}
    <ContentSourcePicker
      open={knowledgePickerOpen}
      onClose={() => setKnowledgePickerOpen(false)}
      companyId={props.homeCompany}
      title="Add knowledge sources"
      showPaste
      showFolders
      files={refs.availableFiles}
      folders={refs.availableFolders}
      modules={modulesData}
      sections={sectionsData}
      selectedFileUuids={refs.selectedFiles}
      selectedFolderUuids={refs.selectedFolders}
      selectedModuleUuids={knowledgeModules}
      selectedSectionUuids={knowledgeSections}
      pastedText={knowledgePastedText}
      onToggleFile={refs.toggleFile}
      onToggleFolder={refs.toggleFolder}
      onToggleModule={handleKnowledgeModuleToggle}
      onToggleSection={handleKnowledgeSectionToggle}
      onPastedTextChange={setKnowledgePastedText}
      onRefreshFiles={refs.refresh}
    />
    {previewOpen && (
      <RoleplayPlayerModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        homeCompany={props.homeCompany}
        previewMode
        previewScenario={previewScenario}
        roleplayName={previewScenario.name}
      />
    )}
    </>
  );
}

export default RoleplayEditorModal;
